import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Plus, Users, LogOut, Book, Settings, Zap, Heart, Pencil, Trash2, RefreshCw, MessageSquare } from 'lucide-react';
import { classroomAPI } from '../utils/api';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import TeacherNotificationBell from '../components/TeacherNotificationBell';
import ThemeToggle from '../components/ThemeToggle';
import { refreshGifCache } from '../utils/helpers';

const TeacherDashboard = () => {
  const navigate = useNavigate();
  const { logout } = useAuth();
  const [classrooms, setClassrooms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [formData, setFormData] = useState({ name: '' });
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingClassroom, setEditingClassroom] = useState(null);
  const [editName, setEditName] = useState('');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deletingClassroom, setDeletingClassroom] = useState(null);

  const loadClassrooms = useCallback(async () => {
    try {
      const response = await classroomAPI.getAll();
      const sortedClassrooms = [...response.data].sort((a, b) => 
        a.name.localeCompare(b.name, 'pt-BR', { numeric: true, sensitivity: 'base' })
      );
      setClassrooms(sortedClassrooms);
    } catch (error) {
      console.error('Failed to load classrooms:', error);
      toast.error('Erro ao carregar salas de aula');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadClassrooms();
  }, [loadClassrooms]);

  // Feedback unread count for navbar badge — polls every 30s AND listens for
  // real-time push via WebSocket so a fresh feedback shows up instantly.
  const [feedbackUnread, setFeedbackUnread] = useState(0);
  useEffect(() => {
    let alive = true;
    const backend = process.env.REACT_APP_BACKEND_URL;
    const url = `${backend}/api/teacher/feedback/unread_count`;
    const fetchCount = async () => {
      try {
        const res = await fetch(url, { credentials: 'include' });
        if (!res.ok) return;
        const d = await res.json();
        if (alive) setFeedbackUnread(d.unread || 0);
      } catch (_) { /* silent */ }
    };
    fetchCount();
    const pollId = setInterval(fetchCount, 30000);

    // Open a websocket to the teacher channel so the badge updates instantly.
    // We identify the teacher via /auth/me — cheap and cached.
    let ws = null;
    let closed = false;
    const connect = async () => {
      try {
        const me = await fetch(`${backend}/api/auth/me`, { credentials: 'include' });
        if (!me.ok) return;
        const info = await me.json();
        const teacherId = info?.user_id;
        if (!teacherId || closed) return;
        const wsUrl = backend.replace(/^http/, 'ws') + `/api/ws/teacher/${teacherId}`;
        ws = new WebSocket(wsUrl);
        ws.onmessage = (ev) => {
          try {
            const payload = JSON.parse(ev.data);
            if (payload?.type === 'feedback:new') {
              // Optimistic bump so the badge feels instant; also refetch as a
              // source-of-truth sync (handles multi-tab teachers).
              setFeedbackUnread((c) => c + 1);
              fetchCount();
              toast.info('Novo feedback recebido!', {
                description: `${payload.data?.user_name} (${payload.data?.classroom_name})`,
              });
            }
          } catch (_) { /* ignore non-JSON pings */ }
        };
      } catch (_) { /* silent */ }
    };
    connect();

    return () => {
      alive = false;
      closed = true;
      clearInterval(pollId);
      if (ws && ws.readyState <= 1) ws.close();
    };
  }, []);

  // Refresh all data including GIF cache
  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      // Refresh GIF cache first
      await refreshGifCache();
      // Reload classrooms data
      await loadClassrooms();
      toast.success('Dados atualizados com sucesso!');
    } catch (error) {
      console.error('Failed to refresh:', error);
      toast.error('Erro ao atualizar dados');
    } finally {
      setRefreshing(false);
    }
  };

  const handleCreateClassroom = async (e) => {
    e.preventDefault();
    try {
      await classroomAPI.create(formData);
      toast.success('Sala de aula criada com sucesso!');
      setDialogOpen(false);
      setFormData({ name: '' });
      loadClassrooms();
    } catch (error) {
      console.error('Failed to create classroom:', error);
      toast.error('Erro ao criar sala de aula');
    }
  };

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/login');
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };

  const openEditDialog = (e, classroom) => {
    e.stopPropagation();
    setEditingClassroom(classroom);
    setEditName(classroom.name);
    setEditDialogOpen(true);
  };

  const handleEditClassroom = async (e) => {
    e.preventDefault();
    if (!editingClassroom) return;
    
    try {
      await classroomAPI.update(editingClassroom.classroom_id, { name: editName });
      
      // Update local state immediately
      setClassrooms(prev => 
        prev.map(c => 
          c.classroom_id === editingClassroom.classroom_id 
            ? { ...c, name: editName } 
            : c
        )
      );
      
      toast.success('Sala renomeada com sucesso!');
      setEditDialogOpen(false);
      setEditingClassroom(null);
    } catch (error) {
      console.error('Failed to update classroom:', error);
      toast.error('Erro ao renomear sala');
    }
  };

  const openDeleteDialog = (e, classroom) => {
    e.stopPropagation();
    setDeletingClassroom(classroom);
    setDeleteDialogOpen(true);
  };

  const handleDeleteClassroom = async () => {
    if (!deletingClassroom) return;
    
    try {
      await classroomAPI.delete(deletingClassroom.classroom_id);
      
      // Update local state immediately
      setClassrooms(prev => 
        prev.filter(c => c.classroom_id !== deletingClassroom.classroom_id)
      );
      
      toast.success('Sala excluída com sucesso!');
      setDeleteDialogOpen(false);
      setDeletingClassroom(null);
    } catch (error) {
      console.error('Failed to delete classroom:', error);
      toast.error('Erro ao excluir sala');
    }
  };

  return (
    <div className="min-h-screen" style={{ backgroundColor: 'var(--bg-primary)' }}>
      <nav className="theme-navbar border-b" style={{ backgroundColor: 'var(--navbar-bg)', borderColor: 'var(--navbar-border)' }}>
        <div className="container mx-auto px-4 py-4 flex justify-between items-center">
          <h1 className="text-2xl font-outfit font-black text-primary" data-testid="teacher-nav-title">BelottoPokeClass Center - Professor</h1>
          <div className="flex items-center gap-2">
            <Button
              data-testid="admin-behaviors-btn"
              onClick={() => navigate('/teacher/admin/behaviors')}
              variant="outline"
              className="flex items-center gap-2"
            >
              <Heart className="w-4 h-4" />
              Comportamentos
            </Button>
            <Button
              data-testid="feedback-btn"
              onClick={() => navigate('/teacher/feedback')}
              variant="outline"
              className="relative flex items-center gap-2"
            >
              <MessageSquare className="w-4 h-4" />
              Feedback
              {feedbackUnread > 0 && (
                <span
                  data-testid="feedback-unread-badge"
                  className="absolute -top-1.5 -right-1.5 min-w-[18px] h-[18px] px-1 rounded-full bg-rose-500 text-white text-[10px] font-black flex items-center justify-center border-2 border-white"
                >
                  {feedbackUnread > 9 ? '9+' : feedbackUnread}
                </span>
              )}
            </Button>
            <Button
              data-testid="admin-powers-btn"
              onClick={() => navigate('/teacher/admin/powers')}
              variant="outline"
              className="flex items-center gap-2"
            >
              <Zap className="w-4 h-4" />
              Poderes
            </Button>
            <Button
              data-testid="admin-gifs-btn"
              onClick={() => navigate('/teacher/admin/gifs')}
              variant="outline"
              className="flex items-center gap-2"
            >
              <Settings className="w-4 h-4" />
              GIFs
            </Button>
            <Button
              data-testid="admin-users-btn"
              onClick={() => navigate('/teacher/admin/users')}
              variant="outline"
              className="flex items-center gap-2"
            >
              <Users className="w-4 h-4" />
              Usuários
            </Button>
            <ThemeToggle />
            <TeacherNotificationBell />
            <Button
              data-testid="logout-btn"
              onClick={handleLogout}
              variant="ghost"
              className="flex items-center gap-2"
            >
              <LogOut className="w-4 h-4" />
              Sair
            </Button>
          </div>
        </div>
      </nav>

      <div className="container mx-auto px-4 py-8">
        <div className="flex justify-between items-center mb-8">
          <div>
            <h2 className="text-3xl font-outfit font-bold text-slate-900 mb-2" data-testid="dashboard-title">Minhas Salas de Aula</h2>
            <p className="text-slate-600 font-nunito">Gerencie suas turmas e acompanhe o progresso dos alunos</p>
          </div>
          <div className="flex items-center gap-3">
            <Button
              data-testid="refresh-data-btn"
              onClick={handleRefresh}
              disabled={refreshing}
              variant="outline"
              className="h-11 px-4 rounded-xl font-bold transition-all flex items-center gap-2"
            >
              <RefreshCw className={`w-5 h-5 ${refreshing ? 'animate-spin' : ''}`} />
              {refreshing ? 'Atualizando...' : 'Atualizar'}
            </Button>
            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button
                data-testid="create-classroom-btn"
                className="bg-primary hover:bg-primary/90 text-white h-11 px-6 rounded-xl font-bold shadow-lg transition-all flex items-center gap-2"
              >
                <Plus className="w-5 h-5" />
                Nova Sala
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle className="font-outfit">Criar Nova Sala de Aula</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleCreateClassroom} className="space-y-4">
                <div>
                  <Label htmlFor="name" className="font-nunito">Nome da Sala</Label>
                  <Input
                    id="name"
                    data-testid="classroom-name-input"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="Ex: Matemática 7º Ano"
                    required
                    className="mt-2"
                  />
                </div>
                <Button
                  data-testid="submit-classroom-btn"
                  type="submit"
                  className="w-full bg-primary hover:bg-primary/90 text-white h-11 rounded-xl font-bold"
                >
                  Criar Sala
                </Button>
              </form>
            </DialogContent>
          </Dialog>
          </div>
        </div>

        {loading ? (
          <div className="flex justify-center py-20">
            <div className="animate-spin rounded-full h-12 w-12 border-b-4 border-primary"></div>
          </div>
        ) : classrooms.length === 0 ? (
          <div className="text-center py-20">
            <Book className="w-16 h-16 text-slate-300 mx-auto mb-4" />
            <p className="text-slate-500 font-nunito">Nenhuma sala criada ainda. Crie sua primeira sala!</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {classrooms.map((classroom) => (
              <div
                key={classroom.classroom_id}
                data-testid="classroom-card"
                onClick={() => navigate(`/teacher/classroom/${classroom.classroom_id}`)}
                className="bg-white rounded-2xl border-2 border-slate-100 p-6 shadow-sm hover:border-blue-200 hover:shadow-md hover:-translate-y-1 transition-all duration-200 cursor-pointer relative group"
              >
                {/* Action buttons - appear on hover */}
                <div className="absolute top-3 right-3 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                  <button
                    data-testid="edit-classroom-btn"
                    onClick={(e) => openEditDialog(e, classroom)}
                    className="p-2 rounded-lg bg-slate-100 hover:bg-blue-100 text-slate-600 hover:text-blue-600 transition-colors"
                    title="Editar sala"
                  >
                    <Pencil className="w-4 h-4" />
                  </button>
                  <button
                    data-testid="delete-classroom-btn"
                    onClick={(e) => openDeleteDialog(e, classroom)}
                    className="p-2 rounded-lg bg-slate-100 hover:bg-red-100 text-slate-600 hover:text-red-600 transition-colors"
                    title="Excluir sala"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
                
                <div className="flex items-start justify-between mb-4">
                  <div className="w-12 h-12 rounded-xl bg-blue-100 flex items-center justify-center">
                    <Users className="w-6 h-6 text-primary" />
                  </div>
                </div>
                <h3 className="text-lg font-outfit font-bold text-slate-900 mb-2">{classroom.name}</h3>
                <div className="flex items-center gap-4 text-sm text-slate-600 font-nunito">
                  <div className="flex items-center gap-1">
                    <Users className="w-4 h-4" />
                    <span>{classroom.students?.length || 0} alunos</span>
                  </div>
                </div>
                <div className="mt-4 pt-4 border-t border-slate-100">
                  <p className="text-xs text-slate-500 font-mono font-bold">Código: {classroom.access_code}</p>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Edit Classroom Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-outfit">Editar Sala de Aula</DialogTitle>
            <DialogDescription>
              Altere o nome da sala de aula.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleEditClassroom} className="space-y-4">
            <div>
              <Label htmlFor="edit-name" className="font-nunito">Nome da Sala</Label>
              <Input
                id="edit-name"
                data-testid="edit-classroom-name-input"
                value={editName}
                onChange={(e) => setEditName(e.target.value)}
                placeholder="Ex: Matemática 7º Ano"
                required
                className="mt-2"
              />
            </div>
            <div className="flex gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => setEditDialogOpen(false)}
                className="flex-1"
              >
                Cancelar
              </Button>
              <Button
                data-testid="confirm-edit-btn"
                type="submit"
                className="flex-1 bg-primary hover:bg-primary/90 text-white"
              >
                Salvar
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-outfit text-red-600">Excluir Sala de Aula</DialogTitle>
            <DialogDescription className="text-slate-600">
              Tem certeza que deseja excluir a sala <strong>"{deletingClassroom?.name}"</strong>? 
              <br /><br />
              <span className="text-red-500 font-semibold">Esta ação não pode ser desfeita.</span> Todos os alunos, monstros, tarefas e dados relacionados serão permanentemente removidos.
            </DialogDescription>
          </DialogHeader>
          <div className="flex gap-2 mt-4">
            <Button
              variant="outline"
              onClick={() => setDeleteDialogOpen(false)}
              className="flex-1"
            >
              Cancelar
            </Button>
            <Button
              data-testid="confirm-delete-btn"
              onClick={handleDeleteClassroom}
              className="flex-1 bg-red-600 hover:bg-red-700 text-white"
            >
              <Trash2 className="w-4 h-4 mr-2" />
              Excluir Sala
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TeacherDashboard;
